import Input from "@/app/components/ui/Inputs/Input";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import { useCategoryDetailAPI } from "@/app/hooks/api_hooks/Inventory/Category/useCategoryDetailAPI";
import { useCategoryUpdateAPI } from "@/app/hooks/api_hooks/Inventory/Category/useCategoryUpdateAPI";
import {
  IProductAddCategoryProps,
  useProductAddServiceCategoryAPI,
} from "@/app/hooks/api_hooks/Inventory/useProductAddServiceCategoryAPI";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useRouter } from "@/app/hooks/useRouter";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";

export interface ICategoryFormProps {
  category_name: string;
}

const CategorySetupOnboard = () => {
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const { forAllAcceptWithOutStartWithDotAndSpace } = useFormValidations();
  const params = useParams();
  const { execute: addCategory } = useProductAddServiceCategoryAPI();
  const { execute: fetchCategoryDetail, categoryDetail } = useCategoryDetailAPI();
  const { execute: updateCategory } = useCategoryUpdateAPI();

  const formHook = useForm<ICategoryFormProps>({
    mode: "onChange",
    defaultValues: {},
  });

  useEffect(() => {
    if (params["category_id"]) {
      fetchCategoryDetail(params["category_id"]);
    }
  }, []);

  useEffect(() => {
    if (categoryDetail) {
      formHook.setValue("category_name", categoryDetail?.category_name);
    }
  }, [categoryDetail]);

  const onSubmit = (data: ICategoryFormProps) => {
    if (data) {
      const constructedBody: IProductAddCategoryProps = {
        category_name: data?.category_name,
      };
      if (params["category_id"]) {
        updateCategory(constructedBody, params["category_id"]).then(() => {
          navigate(`/app/inventory/category-setup`);
        });
      } else {
        addCategory(constructedBody).then((response) => {
          // Destructuring the response directly throws if the API resolves undefined.
          if (response?.status) {
            navigate(`/app/inventory/category-setup`);
          }
        });
      }
    }
  };

  return (
    <>
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-3 rounded-md">
        <div className="sm:flex-auto">
          <h1 className="font-semibold text-base text-gray-900 leading-6">Add New Category</h1>
        </div>
      </div>
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-5 rounded-md">
        <form className="w-full pb-24" onSubmit={formHook.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-4">
              <div className="w-full shrink-0 text-sm sm:w-auto">
                <p>
                  Category Name <span className="text-red-400">*</span>
                </p>
              </div>
              <div className="w-full sm:w-2/5">
                <Input
                  className="border-slate-400 rounded-md w-full h-9 text-sm"
                  inputClassName="h-9"
                  placeholder="Enter Category Name"
                  register={formHook.register("category_name", {
                    required: true,
                    ...forAllAcceptWithOutStartWithDotAndSpace.validations,
                  })}
                  fieldError={formHook.formState.errors.category_name}
                  errorMessages={[
                    { message: "Category Name is required", type: "required" },
                    forAllAcceptWithOutStartWithDotAndSpace.errors,
                  ]}
                />
              </div>
            </div>
          </div>
          <div className="fixed inset-x-0 bottom-0 z-20 flex justify-end items-center gap-4 border-t-2 w-full p-4 bg-white">
            <button
              type="button"
              onClick={() => navigate(getRouteKey("CATEGORY_SETUP", "url"))}
              className={`${SECONDARY_BUTTON} flex-1 sm:flex-none`}
            >
              Back
            </button>
            <button type="submit" className={`${PRIMARY_BUTTON} flex-1 sm:flex-none`}>
              Save
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default CategorySetupOnboard;
